// Multi-language translations for international landing pages

export type Language = 'en' | 'hi' | 'fr' | 'nl' | 'ro' | 'es' | 'de' | 'ar' | 'ja' | 'zh' | 'pt' | 'it' | 'ru' | 'th' | 'vi' | 'tr' | 'sv' | 'no' | 'el' | 'ne' | 'ms' | 'tl';

export interface Translations {
  hero: {
    headline: string;
    description: string;
    getStarted: string;
    connectWithSuppliers: string;
    bySubmitting: string;
  };
  form: {
    fullName: string;
    businessEmail: string;
    phone: string;
    companyName: string;
    monthlyVolume: string;
    selectVolume: string;
    interestedCategories: string;
    under10k: string;
    range10k50k: string;
    range50k100k: string;
    range100k500k: string;
    over500k: string;
  };
  sections: {
    topCategories: string;
    whySource: string;
    verifiedSuppliers: string;
    verifiedSuppliersDesc: string;
    integratedLogistics: string;
    integratedLogisticsDesc: string;
    realTimeTracking: string;
    realTimeTrackingDesc: string;
    readyToStart: string;
    joinThousands: string;
    createFreeAccount: string;
    browseCategories: string;
  };
  stats: {
    tradeVolume: string;
    activeSuppliers: string;
    avgSavings: string;
    avgDelivery: string;
    countriesServed: string;
    qualityCompliance: string;
  };
  nav: {
    home: string;
    categories: string;
    suppliers: string;
    about: string;
    contact: string;
    login: string;
    signup: string;
  };
  common: {
    loading: string;
    error: string;
    success: string;
    submit: string;
    cancel: string;
    save: string;
    delete: string;
    edit: string;
    view: string;
    search: string;
    filter: string;
    noResults: string;
  };
}

export const translations: Record<Language, Translations> = {
  // English (Default)
  en: {
    hero: {
      headline: "Source Quality Products from India",
      description: "Connect with verified Indian suppliers for seamless import. Competitive pricing, quality assurance, and integrated logistics.",
      getStarted: "Get Started Today",
      connectWithSuppliers: "Connect with Suppliers",
      bySubmitting: "By submitting, you agree to our terms of service and privacy policy",
    },
    form: {
      fullName: "Full Name",
      businessEmail: "Business Email",
      phone: "Phone (with country code)",
      companyName: "Company Name",
      monthlyVolume: "Monthly Sourcing Volume",
      selectVolume: "Select volume range",
      interestedCategories: "Interested Categories",
      under10k: "Under $10,000",
      range10k50k: "$10,000 - $50,000",
      range50k100k: "$50,000 - $100,000",
      range100k500k: "$100,000 - $500,000",
      over500k: "Over $500,000",
    },
    sections: {
      topCategories: "Top Export Categories to",
      whySource: "Why Source from India via ProcureSaathi?",
      verifiedSuppliers: "Verified Suppliers",
      verifiedSuppliersDesc: "All suppliers are verified with proper export licenses, certifications, and track records",
      integratedLogistics: "Integrated Logistics",
      integratedLogisticsDesc: "End-to-end shipping solutions from factory to your door with customs support",
      realTimeTracking: "Real-Time Tracking",
      realTimeTrackingDesc: "Track your shipments in real-time from dispatch to delivery at destination port",
      readyToStart: "Ready to Start Sourcing from India?",
      joinThousands: "Join thousands of importers who save 25-40% on procurement costs",
      createFreeAccount: "Create Free Account",
      browseCategories: "Browse Categories",
    },
    stats: {
      tradeVolume: "Trade Volume",
      activeSuppliers: "Active Suppliers",
      avgSavings: "Avg. Savings",
      avgDelivery: "Avg. Delivery Time",
      countriesServed: "Countries Served",
      qualityCompliance: "Quality Compliance",
    },
    nav: {
      home: "Home",
      categories: "Categories",
      suppliers: "Suppliers",
      about: "About",
      contact: "Contact",
      login: "Login",
      signup: "Sign Up",
    },
    common: {
      loading: "Loading...",
      error: "Error",
      success: "Success",
      submit: "Submit",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      view: "View",
      search: "Search",
      filter: "Filter",
      noResults: "No results found",
    },
  },

  // Hindi (India - 85% traffic)
  hi: {
    hero: {
      headline: "भारत से गुणवत्तापूर्ण उत्पाद प्राप्त करें",
      description: "सुगम आयात के लिए सत्यापित भारतीय आपूर्तिकर्ताओं से जुड़ें। प्रतिस्पर्धी मूल्य, गुणवत्ता आश्वासन और एकीकृत लॉजिस्टिक्स।",
      getStarted: "आज ही शुरू करें",
      connectWithSuppliers: "आपूर्तिकर्ताओं से जुड़ें",
      bySubmitting: "सबमिट करके, आप हमारी सेवा की शर्तों और गोपनीयता नीति से सहमत हैं",
    },
    form: {
      fullName: "पूरा नाम",
      businessEmail: "व्यावसायिक ईमेल",
      phone: "फोन (देश कोड के साथ)",
      companyName: "कंपनी का नाम",
      monthlyVolume: "मासिक सोर्सिंग वॉल्यूम",
      selectVolume: "वॉल्यूम रेंज चुनें",
      interestedCategories: "रुचि की श्रेणियाँ",
      under10k: "₹8,00,000 से कम",
      range10k50k: "₹8,00,000 - ₹40,00,000",
      range50k100k: "₹40,00,000 - ₹80,00,000",
      range100k500k: "₹80,00,000 - ₹4,00,00,000",
      over500k: "₹4,00,00,000 से अधिक",
    },
    sections: {
      topCategories: "शीर्ष निर्यात श्रेणियाँ",
      whySource: "ProcureSaathi के माध्यम से भारत से क्यों खरीदें?",
      verifiedSuppliers: "सत्यापित आपूर्तिकर्ता",
      verifiedSuppliersDesc: "सभी आपूर्तिकर्ता उचित निर्यात लाइसेंस, प्रमाणपत्र और ट्रैक रिकॉर्ड के साथ सत्यापित हैं",
      integratedLogistics: "एकीकृत लॉजिस्टिक्स",
      integratedLogisticsDesc: "कारखाने से आपके दरवाजे तक एंड-टू-एंड शिपिंग समाधान",
      realTimeTracking: "रियल-टाइम ट्रैकिंग",
      realTimeTrackingDesc: "डिस्पैच से डिलीवरी तक अपने शिपमेंट को रियल-टाइम में ट्रैक करें",
      readyToStart: "भारत से सोर्सिंग शुरू करने के लिए तैयार हैं?",
      joinThousands: "हजारों आयातकों से जुड़ें जो खरीद लागत पर 25-40% बचाते हैं",
      createFreeAccount: "मुफ्त खाता बनाएं",
      browseCategories: "श्रेणियाँ ब्राउज़ करें",
    },
    stats: {
      tradeVolume: "व्यापार मात्रा",
      activeSuppliers: "सक्रिय आपूर्तिकर्ता",
      avgSavings: "औसत बचत",
      avgDelivery: "औसत डिलीवरी समय",
      countriesServed: "सेवित देश",
      qualityCompliance: "गुणवत्ता अनुपालन",
    },
    nav: {
      home: "होम",
      categories: "श्रेणियाँ",
      suppliers: "आपूर्तिकर्ता",
      about: "हमारे बारे में",
      contact: "संपर्क",
      login: "लॉगिन",
      signup: "साइन अप",
    },
    common: {
      loading: "लोड हो रहा है...",
      error: "त्रुटि",
      success: "सफलता",
      submit: "सबमिट करें",
      cancel: "रद्द करें",
      save: "सेव करें",
      delete: "हटाएं",
      edit: "संपादित करें",
      view: "देखें",
      search: "खोजें",
      filter: "फ़िल्टर",
      noResults: "कोई परिणाम नहीं मिला",
    },
  },

  // French (France 2%, Canada)
  fr: {
    hero: {
      headline: "Approvisionnez-vous en produits de qualité depuis l'Inde",
      description: "Connectez-vous avec des fournisseurs indiens vérifiés pour une importation fluide. Prix compétitifs, assurance qualité et logistique intégrée.",
      getStarted: "Commencer aujourd'hui",
      connectWithSuppliers: "Connecter avec les fournisseurs",
      bySubmitting: "En soumettant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité",
    },
    form: {
      fullName: "Nom complet",
      businessEmail: "Email professionnel",
      phone: "Téléphone (avec indicatif pays)",
      companyName: "Nom de l'entreprise",
      monthlyVolume: "Volume d'approvisionnement mensuel",
      selectVolume: "Sélectionner la plage de volume",
      interestedCategories: "Catégories d'intérêt",
      under10k: "Moins de 10 000 €",
      range10k50k: "10 000 € - 50 000 €",
      range50k100k: "50 000 € - 100 000 €",
      range100k500k: "100 000 € - 500 000 €",
      over500k: "Plus de 500 000 €",
    },
    sections: {
      topCategories: "Principales catégories d'exportation vers",
      whySource: "Pourquoi s'approvisionner depuis l'Inde via ProcureSaathi?",
      verifiedSuppliers: "Fournisseurs vérifiés",
      verifiedSuppliersDesc: "Tous les fournisseurs sont vérifiés avec des licences d'exportation, certifications et références appropriées",
      integratedLogistics: "Logistique intégrée",
      integratedLogisticsDesc: "Solutions d'expédition de bout en bout de l'usine à votre porte avec support douanier",
      realTimeTracking: "Suivi en temps réel",
      realTimeTrackingDesc: "Suivez vos expéditions en temps réel de l'envoi à la livraison au port de destination",
      readyToStart: "Prêt à commencer l'approvisionnement depuis l'Inde?",
      joinThousands: "Rejoignez des milliers d'importateurs qui économisent 25-40% sur les coûts d'approvisionnement",
      createFreeAccount: "Créer un compte gratuit",
      browseCategories: "Parcourir les catégories",
    },
    stats: {
      tradeVolume: "Volume commercial",
      activeSuppliers: "Fournisseurs actifs",
      avgSavings: "Économies moy.",
      avgDelivery: "Délai de livraison moy.",
      countriesServed: "Pays desservis",
      qualityCompliance: "Conformité qualité",
    },
    nav: {
      home: "Accueil",
      categories: "Catégories",
      suppliers: "Fournisseurs",
      about: "À propos",
      contact: "Contact",
      login: "Connexion",
      signup: "Inscription",
    },
    common: {
      loading: "Chargement...",
      error: "Erreur",
      success: "Succès",
      submit: "Soumettre",
      cancel: "Annuler",
      save: "Enregistrer",
      delete: "Supprimer",
      edit: "Modifier",
      view: "Voir",
      search: "Rechercher",
      filter: "Filtrer",
      noResults: "Aucun résultat trouvé",
    },
  },

  // Dutch (Netherlands 1%)
  nl: {
    hero: {
      headline: "Kwaliteitsproducten uit India",
      description: "Verbind met geverifieerde Indiase leveranciers voor naadloze import. Concurrerende prijzen, kwaliteitsborging en geïntegreerde logistiek.",
      getStarted: "Begin vandaag",
      connectWithSuppliers: "Verbind met leveranciers",
      bySubmitting: "Door in te dienen gaat u akkoord met onze servicevoorwaarden en privacybeleid",
    },
    form: {
      fullName: "Volledige naam",
      businessEmail: "Zakelijke e-mail",
      phone: "Telefoon (met landcode)",
      companyName: "Bedrijfsnaam",
      monthlyVolume: "Maandelijks inkoopvolume",
      selectVolume: "Selecteer volumebereik",
      interestedCategories: "Geïnteresseerde categorieën",
      under10k: "Onder €10.000",
      range10k50k: "€10.000 - €50.000",
      range50k100k: "€50.000 - €100.000",
      range100k500k: "€100.000 - €500.000",
      over500k: "Boven €500.000",
    },
    sections: {
      topCategories: "Top exportcategorieën naar",
      whySource: "Waarom via ProcureSaathi uit India inkopen?",
      verifiedSuppliers: "Geverifieerde leveranciers",
      verifiedSuppliersDesc: "Alle leveranciers zijn geverifieerd met juiste exportlicenties, certificeringen en trackrecords",
      integratedLogistics: "Geïntegreerde logistiek",
      integratedLogisticsDesc: "End-to-end verzendoplossingen van fabriek tot uw deur met douaneondersteuning",
      realTimeTracking: "Realtime tracking",
      realTimeTrackingDesc: "Volg uw zendingen in realtime van verzending tot levering in de bestemmingshaven",
      readyToStart: "Klaar om te beginnen met inkopen uit India?",
      joinThousands: "Sluit u aan bij duizenden importeurs die 25-40% besparen op inkoopkosten",
      createFreeAccount: "Gratis account aanmaken",
      browseCategories: "Categorieën bekijken",
    },
    stats: {
      tradeVolume: "Handelsvolume",
      activeSuppliers: "Actieve leveranciers",
      avgSavings: "Gem. besparing",
      avgDelivery: "Gem. levertijd",
      countriesServed: "Bediende landen",
      qualityCompliance: "Kwaliteitsconformiteit",
    },
    nav: {
      home: "Home",
      categories: "Categorieën",
      suppliers: "Leveranciers",
      about: "Over ons",
      contact: "Contact",
      login: "Inloggen",
      signup: "Registreren",
    },
    common: {
      loading: "Laden...",
      error: "Fout",
      success: "Succes",
      submit: "Verzenden",
      cancel: "Annuleren",
      save: "Opslaan",
      delete: "Verwijderen",
      edit: "Bewerken",
      view: "Bekijken",
      search: "Zoeken",
      filter: "Filteren",
      noResults: "Geen resultaten gevonden",
    },
  },

  // Romanian (Romania 1%)
  ro: {
    hero: {
      headline: "Achiziționați produse de calitate din India",
      description: "Conectați-vă cu furnizori indieni verificați pentru import fără probleme. Prețuri competitive, asigurarea calității și logistică integrată.",
      getStarted: "Începeți astăzi",
      connectWithSuppliers: "Conectați-vă cu furnizorii",
      bySubmitting: "Prin trimitere, sunteți de acord cu termenii serviciului și politica de confidențialitate",
    },
    form: {
      fullName: "Nume complet",
      businessEmail: "Email de afaceri",
      phone: "Telefon (cu codul țării)",
      companyName: "Numele companiei",
      monthlyVolume: "Volumul lunar de achiziții",
      selectVolume: "Selectați intervalul de volum",
      interestedCategories: "Categorii de interes",
      under10k: "Sub 10.000 €",
      range10k50k: "10.000 € - 50.000 €",
      range50k100k: "50.000 € - 100.000 €",
      range100k500k: "100.000 € - 500.000 €",
      over500k: "Peste 500.000 €",
    },
    sections: {
      topCategories: "Categorii principale de export către",
      whySource: "De ce să achiziționați din India prin ProcureSaathi?",
      verifiedSuppliers: "Furnizori verificați",
      verifiedSuppliersDesc: "Toți furnizorii sunt verificați cu licențe de export, certificări și referințe corespunzătoare",
      integratedLogistics: "Logistică integrată",
      integratedLogisticsDesc: "Soluții de expediere de la capăt la capăt de la fabrică la ușa dvs. cu suport vamal",
      realTimeTracking: "Urmărire în timp real",
      realTimeTrackingDesc: "Urmăriți expedițiile în timp real de la expediere până la livrare în portul de destinație",
      readyToStart: "Sunteți gata să începeți achizițiile din India?",
      joinThousands: "Alăturați-vă miilor de importatori care economisesc 25-40% din costurile de achiziție",
      createFreeAccount: "Creați un cont gratuit",
      browseCategories: "Răsfoiți categoriile",
    },
    stats: {
      tradeVolume: "Volumul comerțului",
      activeSuppliers: "Furnizori activi",
      avgSavings: "Economii medii",
      avgDelivery: "Timp mediu de livrare",
      countriesServed: "Țări deservite",
      qualityCompliance: "Conformitate calitate",
    },
    nav: {
      home: "Acasă",
      categories: "Categorii",
      suppliers: "Furnizori",
      about: "Despre noi",
      contact: "Contact",
      login: "Autentificare",
      signup: "Înregistrare",
    },
    common: {
      loading: "Se încarcă...",
      error: "Eroare",
      success: "Succes",
      submit: "Trimite",
      cancel: "Anulează",
      save: "Salvează",
      delete: "Șterge",
      edit: "Editează",
      view: "Vizualizare",
      search: "Caută",
      filter: "Filtrează",
      noResults: "Nu s-au găsit rezultate",
    },
  },

  // Spanish (Spain 1%)
  es: {
    hero: {
      headline: "Obtenga productos de calidad desde India",
      description: "Conéctese con proveedores indios verificados para una importación sin problemas. Precios competitivos, garantía de calidad y logística integrada.",
      getStarted: "Comience hoy",
      connectWithSuppliers: "Conectar con proveedores",
      bySubmitting: "Al enviar, acepta nuestros términos de servicio y política de privacidad",
    },
    form: {
      fullName: "Nombre completo",
      businessEmail: "Correo electrónico empresarial",
      phone: "Teléfono (con código de país)",
      companyName: "Nombre de la empresa",
      monthlyVolume: "Volumen de abastecimiento mensual",
      selectVolume: "Seleccionar rango de volumen",
      interestedCategories: "Categorías de interés",
      under10k: "Menos de 10.000 €",
      range10k50k: "10.000 € - 50.000 €",
      range50k100k: "50.000 € - 100.000 €",
      range100k500k: "100.000 € - 500.000 €",
      over500k: "Más de 500.000 €",
    },
    sections: {
      topCategories: "Principales categorías de exportación a",
      whySource: "¿Por qué abastecerse desde India a través de ProcureSaathi?",
      verifiedSuppliers: "Proveedores verificados",
      verifiedSuppliersDesc: "Todos los proveedores están verificados con licencias de exportación, certificaciones y referencias adecuadas",
      integratedLogistics: "Logística integrada",
      integratedLogisticsDesc: "Soluciones de envío de extremo a extremo desde la fábrica hasta su puerta con soporte aduanero",
      realTimeTracking: "Seguimiento en tiempo real",
      realTimeTrackingDesc: "Rastree sus envíos en tiempo real desde el despacho hasta la entrega en el puerto de destino",
      readyToStart: "¿Listo para comenzar a abastecerse desde India?",
      joinThousands: "Únase a miles de importadores que ahorran 25-40% en costos de adquisición",
      createFreeAccount: "Crear cuenta gratuita",
      browseCategories: "Explorar categorías",
    },
    stats: {
      tradeVolume: "Volumen comercial",
      activeSuppliers: "Proveedores activos",
      avgSavings: "Ahorro promedio",
      avgDelivery: "Tiempo de entrega prom.",
      countriesServed: "Países atendidos",
      qualityCompliance: "Cumplimiento de calidad",
    },
    nav: {
      home: "Inicio",
      categories: "Categorías",
      suppliers: "Proveedores",
      about: "Acerca de",
      contact: "Contacto",
      login: "Iniciar sesión",
      signup: "Registrarse",
    },
    common: {
      loading: "Cargando...",
      error: "Error",
      success: "Éxito",
      submit: "Enviar",
      cancel: "Cancelar",
      save: "Guardar",
      delete: "Eliminar",
      edit: "Editar",
      view: "Ver",
      search: "Buscar",
      filter: "Filtrar",
      noResults: "No se encontraron resultados",
    },
  },

  // German (Germany)
  de: {
    hero: {
      headline: "Qualitätsprodukte aus Indien beziehen",
      description: "Verbinden Sie sich mit verifizierten indischen Lieferanten für nahtlosen Import. Wettbewerbsfähige Preise, Qualitätssicherung und integrierte Logistik.",
      getStarted: "Jetzt starten",
      connectWithSuppliers: "Mit Lieferanten verbinden",
      bySubmitting: "Mit der Übermittlung stimmen Sie unseren Nutzungsbedingungen und Datenschutzrichtlinien zu",
    },
    form: {
      fullName: "Vollständiger Name",
      businessEmail: "Geschäftliche E-Mail",
      phone: "Telefon (mit Landesvorwahl)",
      companyName: "Firmenname",
      monthlyVolume: "Monatliches Beschaffungsvolumen",
      selectVolume: "Volumenbereich auswählen",
      interestedCategories: "Interessierte Kategorien",
      under10k: "Unter 10.000 €",
      range10k50k: "10.000 € - 50.000 €",
      range50k100k: "50.000 € - 100.000 €",
      range100k500k: "100.000 € - 500.000 €",
      over500k: "Über 500.000 €",
    },
    sections: {
      topCategories: "Top-Exportkategorien nach",
      whySource: "Warum über ProcureSaathi aus Indien beziehen?",
      verifiedSuppliers: "Verifizierte Lieferanten",
      verifiedSuppliersDesc: "Alle Lieferanten sind mit ordnungsgemäßen Exportlizenzen, Zertifizierungen und Erfolgsnachweisen verifiziert",
      integratedLogistics: "Integrierte Logistik",
      integratedLogisticsDesc: "End-to-End-Versandlösungen von der Fabrik bis zu Ihrer Tür mit Zollunterstützung",
      realTimeTracking: "Echtzeit-Verfolgung",
      realTimeTrackingDesc: "Verfolgen Sie Ihre Sendungen in Echtzeit vom Versand bis zur Lieferung am Zielhafen",
      readyToStart: "Bereit, mit der Beschaffung aus Indien zu beginnen?",
      joinThousands: "Schließen Sie sich Tausenden von Importeuren an, die 25-40% bei den Beschaffungskosten sparen",
      createFreeAccount: "Kostenloses Konto erstellen",
      browseCategories: "Kategorien durchsuchen",
    },
    stats: {
      tradeVolume: "Handelsvolumen",
      activeSuppliers: "Aktive Lieferanten",
      avgSavings: "Durchschn. Ersparnis",
      avgDelivery: "Durchschn. Lieferzeit",
      countriesServed: "Bediente Länder",
      qualityCompliance: "Qualitätskonformität",
    },
    nav: {
      home: "Startseite",
      categories: "Kategorien",
      suppliers: "Lieferanten",
      about: "Über uns",
      contact: "Kontakt",
      login: "Anmelden",
      signup: "Registrieren",
    },
    common: {
      loading: "Wird geladen...",
      error: "Fehler",
      success: "Erfolg",
      submit: "Absenden",
      cancel: "Abbrechen",
      save: "Speichern",
      delete: "Löschen",
      edit: "Bearbeiten",
      view: "Ansehen",
      search: "Suchen",
      filter: "Filtern",
      noResults: "Keine Ergebnisse gefunden",
    },
  },

  // Arabic (UAE)
  ar: {
    hero: {
      headline: "احصل على منتجات عالية الجودة من الهند",
      description: "تواصل مع موردين هنود معتمدين للاستيراد السلس. أسعار تنافسية، ضمان الجودة، وخدمات لوجستية متكاملة.",
      getStarted: "ابدأ اليوم",
      connectWithSuppliers: "تواصل مع الموردين",
      bySubmitting: "بتقديم هذا النموذج، أنت توافق على شروط الخدمة وسياسة الخصوصية",
    },
    form: {
      fullName: "الاسم الكامل",
      businessEmail: "البريد الإلكتروني للعمل",
      phone: "الهاتف (مع رمز الدولة)",
      companyName: "اسم الشركة",
      monthlyVolume: "حجم التوريد الشهري",
      selectVolume: "اختر نطاق الحجم",
      interestedCategories: "الفئات المهتمة بها",
      under10k: "أقل من 10,000 دولار",
      range10k50k: "10,000 - 50,000 دولار",
      range50k100k: "50,000 - 100,000 دولار",
      range100k500k: "100,000 - 500,000 دولار",
      over500k: "أكثر من 500,000 دولار",
    },
    sections: {
      topCategories: "أهم فئات التصدير إلى",
      whySource: "لماذا تشتري من الهند عبر ProcureSaathi؟",
      verifiedSuppliers: "موردون معتمدون",
      verifiedSuppliersDesc: "جميع الموردين معتمدون بتراخيص تصدير مناسبة وشهادات وسجلات أداء",
      integratedLogistics: "خدمات لوجستية متكاملة",
      integratedLogisticsDesc: "حلول شحن شاملة من المصنع إلى بابك مع دعم جمركي",
      realTimeTracking: "تتبع في الوقت الفعلي",
      realTimeTrackingDesc: "تتبع شحناتك في الوقت الفعلي من الإرسال إلى التسليم في الميناء",
      readyToStart: "هل أنت مستعد للبدء بالاستيراد من الهند؟",
      joinThousands: "انضم إلى آلاف المستوردين الذين يوفرون 25-40% من تكاليف الشراء",
      createFreeAccount: "إنشاء حساب مجاني",
      browseCategories: "تصفح الفئات",
    },
    stats: {
      tradeVolume: "حجم التجارة",
      activeSuppliers: "الموردون النشطون",
      avgSavings: "متوسط التوفير",
      avgDelivery: "متوسط وقت التسليم",
      countriesServed: "الدول المخدومة",
      qualityCompliance: "الامتثال للجودة",
    },
    nav: {
      home: "الرئيسية",
      categories: "الفئات",
      suppliers: "الموردون",
      about: "من نحن",
      contact: "اتصل بنا",
      login: "تسجيل الدخول",
      signup: "إنشاء حساب",
    },
    common: {
      loading: "جاري التحميل...",
      error: "خطأ",
      success: "نجاح",
      submit: "إرسال",
      cancel: "إلغاء",
      save: "حفظ",
      delete: "حذف",
      edit: "تعديل",
      view: "عرض",
      search: "بحث",
      filter: "تصفية",
      noResults: "لم يتم العثور على نتائج",
    },
  },

  // Japanese
  ja: {
    hero: {
      headline: "インドから高品質な製品を調達",
      description: "認証済みのインドサプライヤーとスムーズな輸入を実現。競争力のある価格、品質保証、統合ロジスティクス。",
      getStarted: "今すぐ始める",
      connectWithSuppliers: "サプライヤーと繋がる",
      bySubmitting: "送信することで、利用規約とプライバシーポリシーに同意します",
    },
    form: {
      fullName: "氏名",
      businessEmail: "ビジネスメール",
      phone: "電話（国番号付き）",
      companyName: "会社名",
      monthlyVolume: "月間調達量",
      selectVolume: "数量範囲を選択",
      interestedCategories: "関心のあるカテゴリ",
      under10k: "100万円未満",
      range10k50k: "100万円〜500万円",
      range50k100k: "500万円〜1000万円",
      range100k500k: "1000万円〜5000万円",
      over500k: "5000万円以上",
    },
    sections: {
      topCategories: "への主要輸出カテゴリ",
      whySource: "なぜProcureSaathiでインドから調達するのか？",
      verifiedSuppliers: "認証済みサプライヤー",
      verifiedSuppliersDesc: "すべてのサプライヤーは適切な輸出ライセンス、認証、実績を持っています",
      integratedLogistics: "統合ロジスティクス",
      integratedLogisticsDesc: "工場からお客様のもとまで、通関サポート付きのエンドツーエンドの配送ソリューション",
      realTimeTracking: "リアルタイム追跡",
      realTimeTrackingDesc: "発送から目的地港への配送までリアルタイムで追跡",
      readyToStart: "インドからの調達を始める準備はできましたか？",
      joinThousands: "調達コストを25-40%節約している何千もの輸入業者に参加",
      createFreeAccount: "無料アカウント作成",
      browseCategories: "カテゴリを閲覧",
    },
    stats: {
      tradeVolume: "貿易額",
      activeSuppliers: "アクティブサプライヤー",
      avgSavings: "平均節約",
      avgDelivery: "平均配送時間",
      countriesServed: "対応国数",
      qualityCompliance: "品質コンプライアンス",
    },
    nav: {
      home: "ホーム",
      categories: "カテゴリ",
      suppliers: "サプライヤー",
      about: "会社概要",
      contact: "お問い合わせ",
      login: "ログイン",
      signup: "新規登録",
    },
    common: {
      loading: "読み込み中...",
      error: "エラー",
      success: "成功",
      submit: "送信",
      cancel: "キャンセル",
      save: "保存",
      delete: "削除",
      edit: "編集",
      view: "表示",
      search: "検索",
      filter: "フィルター",
      noResults: "結果が見つかりません",
    },
  },

  // Chinese (Simplified)
  zh: {
    hero: {
      headline: "从印度采购优质产品",
      description: "与经过验证的印度供应商建立联系，实现无缝进口。具有竞争力的价格、质量保证和集成物流。",
      getStarted: "立即开始",
      connectWithSuppliers: "联系供应商",
      bySubmitting: "提交即表示您同意我们的服务条款和隐私政策",
    },
    form: {
      fullName: "全名",
      businessEmail: "商务邮箱",
      phone: "电话（含国家代码）",
      companyName: "公司名称",
      monthlyVolume: "月采购量",
      selectVolume: "选择数量范围",
      interestedCategories: "感兴趣的类别",
      under10k: "低于7万元",
      range10k50k: "7万-35万元",
      range50k100k: "35万-70万元",
      range100k500k: "70万-350万元",
      over500k: "超过350万元",
    },
    sections: {
      topCategories: "对的主要出口类别",
      whySource: "为什么通过ProcureSaathi从印度采购？",
      verifiedSuppliers: "认证供应商",
      verifiedSuppliersDesc: "所有供应商都经过验证，拥有适当的出口许可证、认证和业绩记录",
      integratedLogistics: "集成物流",
      integratedLogisticsDesc: "从工厂到您门口的端到端运输解决方案，包括海关支持",
      realTimeTracking: "实时跟踪",
      realTimeTrackingDesc: "从发货到目的港交付，实时跟踪您的货物",
      readyToStart: "准备好开始从印度采购了吗？",
      joinThousands: "加入数千家节省25-40%采购成本的进口商",
      createFreeAccount: "创建免费账户",
      browseCategories: "浏览类别",
    },
    stats: {
      tradeVolume: "贸易额",
      activeSuppliers: "活跃供应商",
      avgSavings: "平均节省",
      avgDelivery: "平均交货时间",
      countriesServed: "服务国家",
      qualityCompliance: "质量合规",
    },
    nav: {
      home: "首页",
      categories: "类别",
      suppliers: "供应商",
      about: "关于我们",
      contact: "联系我们",
      login: "登录",
      signup: "注册",
    },
    common: {
      loading: "加载中...",
      error: "错误",
      success: "成功",
      submit: "提交",
      cancel: "取消",
      save: "保存",
      delete: "删除",
      edit: "编辑",
      view: "查看",
      search: "搜索",
      filter: "筛选",
      noResults: "未找到结果",
    },
  },

  // Portuguese
  pt: {
    hero: {
      headline: "Adquira produtos de qualidade da Índia",
      description: "Conecte-se com fornecedores indianos verificados para importação sem problemas. Preços competitivos, garantia de qualidade e logística integrada.",
      getStarted: "Comece hoje",
      connectWithSuppliers: "Conectar com fornecedores",
      bySubmitting: "Ao enviar, você concorda com nossos termos de serviço e política de privacidade",
    },
    form: {
      fullName: "Nome completo",
      businessEmail: "Email comercial",
      phone: "Telefone (com código do país)",
      companyName: "Nome da empresa",
      monthlyVolume: "Volume mensal de aquisição",
      selectVolume: "Selecione a faixa de volume",
      interestedCategories: "Categorias de interesse",
      under10k: "Menos de €10.000",
      range10k50k: "€10.000 - €50.000",
      range50k100k: "€50.000 - €100.000",
      range100k500k: "€100.000 - €500.000",
      over500k: "Mais de €500.000",
    },
    sections: {
      topCategories: "Principais categorias de exportação para",
      whySource: "Por que adquirir da Índia via ProcureSaathi?",
      verifiedSuppliers: "Fornecedores verificados",
      verifiedSuppliersDesc: "Todos os fornecedores são verificados com licenças de exportação, certificações e histórico adequados",
      integratedLogistics: "Logística integrada",
      integratedLogisticsDesc: "Soluções de envio de ponta a ponta da fábrica à sua porta com suporte aduaneiro",
      realTimeTracking: "Rastreamento em tempo real",
      realTimeTrackingDesc: "Rastreie seus envios em tempo real desde o despacho até a entrega no porto de destino",
      readyToStart: "Pronto para começar a adquirir da Índia?",
      joinThousands: "Junte-se a milhares de importadores que economizam 25-40% nos custos de aquisição",
      createFreeAccount: "Criar conta gratuita",
      browseCategories: "Explorar categorias",
    },
    stats: {
      tradeVolume: "Volume comercial",
      activeSuppliers: "Fornecedores ativos",
      avgSavings: "Economia média",
      avgDelivery: "Prazo médio de entrega",
      countriesServed: "Países atendidos",
      qualityCompliance: "Conformidade de qualidade",
    },
    nav: {
      home: "Início",
      categories: "Categorias",
      suppliers: "Fornecedores",
      about: "Sobre",
      contact: "Contato",
      login: "Entrar",
      signup: "Cadastrar",
    },
    common: {
      loading: "Carregando...",
      error: "Erro",
      success: "Sucesso",
      submit: "Enviar",
      cancel: "Cancelar",
      save: "Salvar",
      delete: "Excluir",
      edit: "Editar",
      view: "Ver",
      search: "Pesquisar",
      filter: "Filtrar",
      noResults: "Nenhum resultado encontrado",
    },
  },

  // Italian
  it: {
    hero: {
      headline: "Acquista prodotti di qualità dall'India",
      description: "Connettiti con fornitori indiani verificati per un'importazione senza problemi. Prezzi competitivi, garanzia di qualità e logistica integrata.",
      getStarted: "Inizia oggi",
      connectWithSuppliers: "Connetti con i fornitori",
      bySubmitting: "Inviando, accetti i nostri termini di servizio e la politica sulla privacy",
    },
    form: {
      fullName: "Nome completo",
      businessEmail: "Email aziendale",
      phone: "Telefono (con prefisso paese)",
      companyName: "Nome azienda",
      monthlyVolume: "Volume mensile di approvvigionamento",
      selectVolume: "Seleziona intervallo volume",
      interestedCategories: "Categorie di interesse",
      under10k: "Meno di €10.000",
      range10k50k: "€10.000 - €50.000",
      range50k100k: "€50.000 - €100.000",
      range100k500k: "€100.000 - €500.000",
      over500k: "Oltre €500.000",
    },
    sections: {
      topCategories: "Principali categorie di esportazione verso",
      whySource: "Perché acquistare dall'India tramite ProcureSaathi?",
      verifiedSuppliers: "Fornitori verificati",
      verifiedSuppliersDesc: "Tutti i fornitori sono verificati con licenze di esportazione, certificazioni e track record appropriati",
      integratedLogistics: "Logistica integrata",
      integratedLogisticsDesc: "Soluzioni di spedizione end-to-end dalla fabbrica alla tua porta con supporto doganale",
      realTimeTracking: "Tracciamento in tempo reale",
      realTimeTrackingDesc: "Traccia le tue spedizioni in tempo reale dalla spedizione alla consegna al porto di destinazione",
      readyToStart: "Pronto per iniziare ad acquistare dall'India?",
      joinThousands: "Unisciti a migliaia di importatori che risparmiano il 25-40% sui costi di approvvigionamento",
      createFreeAccount: "Crea account gratuito",
      browseCategories: "Sfoglia categorie",
    },
    stats: {
      tradeVolume: "Volume commerciale",
      activeSuppliers: "Fornitori attivi",
      avgSavings: "Risparmio medio",
      avgDelivery: "Tempo medio di consegna",
      countriesServed: "Paesi serviti",
      qualityCompliance: "Conformità qualità",
    },
    nav: {
      home: "Home",
      categories: "Categorie",
      suppliers: "Fornitori",
      about: "Chi siamo",
      contact: "Contatti",
      login: "Accedi",
      signup: "Registrati",
    },
    common: {
      loading: "Caricamento...",
      error: "Errore",
      success: "Successo",
      submit: "Invia",
      cancel: "Annulla",
      save: "Salva",
      delete: "Elimina",
      edit: "Modifica",
      view: "Visualizza",
      search: "Cerca",
      filter: "Filtra",
      noResults: "Nessun risultato trovato",
    },
  },

  // Russian
  ru: {
    hero: {
      headline: "Закупайте качественную продукцию из Индии",
      description: "Свяжитесь с проверенными индийскими поставщиками для беспроблемного импорта. Конкурентные цены, гарантия качества и интегрированная логистика.",
      getStarted: "Начать сегодня",
      connectWithSuppliers: "Связаться с поставщиками",
      bySubmitting: "Отправляя заявку, вы соглашаетесь с условиями использования и политикой конфиденциальности",
    },
    form: {
      fullName: "Полное имя",
      businessEmail: "Рабочий email",
      phone: "Телефон (с кодом страны)",
      companyName: "Название компании",
      monthlyVolume: "Ежемесячный объем закупок",
      selectVolume: "Выберите диапазон объема",
      interestedCategories: "Интересующие категории",
      under10k: "До $10,000",
      range10k50k: "$10,000 - $50,000",
      range50k100k: "$50,000 - $100,000",
      range100k500k: "$100,000 - $500,000",
      over500k: "Свыше $500,000",
    },
    sections: {
      topCategories: "Основные категории экспорта в",
      whySource: "Почему закупать из Индии через ProcureSaathi?",
      verifiedSuppliers: "Проверенные поставщики",
      verifiedSuppliersDesc: "Все поставщики проверены с соответствующими экспортными лицензиями, сертификатами и репутацией",
      integratedLogistics: "Интегрированная логистика",
      integratedLogisticsDesc: "Комплексные решения по доставке от завода до вашей двери с таможенной поддержкой",
      realTimeTracking: "Отслеживание в реальном времени",
      realTimeTrackingDesc: "Отслеживайте ваши отправления в реальном времени от отправки до доставки в порт назначения",
      readyToStart: "Готовы начать закупки из Индии?",
      joinThousands: "Присоединяйтесь к тысячам импортеров, экономящих 25-40% на закупках",
      createFreeAccount: "Создать бесплатный аккаунт",
      browseCategories: "Просмотреть категории",
    },
    stats: {
      tradeVolume: "Объем торговли",
      activeSuppliers: "Активные поставщики",
      avgSavings: "Средняя экономия",
      avgDelivery: "Среднее время доставки",
      countriesServed: "Обслуживаемые страны",
      qualityCompliance: "Соответствие качеству",
    },
    nav: {
      home: "Главная",
      categories: "Категории",
      suppliers: "Поставщики",
      about: "О нас",
      contact: "Контакты",
      login: "Вход",
      signup: "Регистрация",
    },
    common: {
      loading: "Загрузка...",
      error: "Ошибка",
      success: "Успешно",
      submit: "Отправить",
      cancel: "Отмена",
      save: "Сохранить",
      delete: "Удалить",
      edit: "Редактировать",
      view: "Просмотр",
      search: "Поиск",
      filter: "Фильтр",
      noResults: "Результаты не найдены",
    },
  },

  // Thai
  th: {
    hero: {
      headline: "จัดหาสินค้าคุณภาพจากอินเดีย",
      description: "เชื่อมต่อกับซัพพลายเออร์อินเดียที่ได้รับการรับรองเพื่อการนำเข้าที่ราบรื่น ราคาแข่งขัน การรับประกันคุณภาพ และโลจิสติกส์ครบวงจร",
      getStarted: "เริ่มต้นวันนี้",
      connectWithSuppliers: "เชื่อมต่อกับซัพพลายเออร์",
      bySubmitting: "การส่งข้อมูล ถือว่าท่านยอมรับข้อกำหนดการใช้บริการและนโยบายความเป็นส่วนตัว",
    },
    form: {
      fullName: "ชื่อ-นามสกุล",
      businessEmail: "อีเมลธุรกิจ",
      phone: "โทรศัพท์ (พร้อมรหัสประเทศ)",
      companyName: "ชื่อบริษัท",
      monthlyVolume: "ปริมาณการจัดซื้อรายเดือน",
      selectVolume: "เลือกช่วงปริมาณ",
      interestedCategories: "หมวดหมู่ที่สนใจ",
      under10k: "ต่ำกว่า ฿350,000",
      range10k50k: "฿350,000 - ฿1,750,000",
      range50k100k: "฿1,750,000 - ฿3,500,000",
      range100k500k: "฿3,500,000 - ฿17,500,000",
      over500k: "มากกว่า ฿17,500,000",
    },
    sections: {
      topCategories: "หมวดหมู่ส่งออกหลักไปยัง",
      whySource: "ทำไมต้องจัดหาจากอินเดียผ่าน ProcureSaathi?",
      verifiedSuppliers: "ซัพพลายเออร์ที่ได้รับการรับรอง",
      verifiedSuppliersDesc: "ซัพพลายเออร์ทุกรายได้รับการตรวจสอบพร้อมใบอนุญาตส่งออก ใบรับรอง และประวัติที่เหมาะสม",
      integratedLogistics: "โลจิสติกส์ครบวงจร",
      integratedLogisticsDesc: "โซลูชันการจัดส่งครบวงจรจากโรงงานถึงประตูบ้านคุณพร้อมการสนับสนุนด้านศุลกากร",
      realTimeTracking: "ติดตามแบบเรียลไทม์",
      realTimeTrackingDesc: "ติดตามการจัดส่งของคุณแบบเรียลไทม์ตั้งแต่จัดส่งจนถึงปลายทาง",
      readyToStart: "พร้อมเริ่มต้นจัดหาจากอินเดียแล้วหรือยัง?",
      joinThousands: "เข้าร่วมกับผู้นำเข้าหลายพันรายที่ประหยัดค่าจัดซื้อ 25-40%",
      createFreeAccount: "สร้างบัญชีฟรี",
      browseCategories: "ดูหมวดหมู่",
    },
    stats: {
      tradeVolume: "ปริมาณการค้า",
      activeSuppliers: "ซัพพลายเออร์ที่ใช้งาน",
      avgSavings: "ประหยัดเฉลี่ย",
      avgDelivery: "เวลาจัดส่งเฉลี่ย",
      countriesServed: "ประเทศที่ให้บริการ",
      qualityCompliance: "การปฏิบัติตามคุณภาพ",
    },
    nav: {
      home: "หน้าแรก",
      categories: "หมวดหมู่",
      suppliers: "ซัพพลายเออร์",
      about: "เกี่ยวกับเรา",
      contact: "ติดต่อ",
      login: "เข้าสู่ระบบ",
      signup: "สมัครสมาชิก",
    },
    common: {
      loading: "กำลังโหลด...",
      error: "ข้อผิดพลาด",
      success: "สำเร็จ",
      submit: "ส่ง",
      cancel: "ยกเลิก",
      save: "บันทึก",
      delete: "ลบ",
      edit: "แก้ไข",
      view: "ดู",
      search: "ค้นหา",
      filter: "กรอง",
      noResults: "ไม่พบผลลัพธ์",
    },
  },

  // Vietnamese
  vi: {
    hero: {
      headline: "Nguồn cung sản phẩm chất lượng từ Ấn Độ",
      description: "Kết nối với các nhà cung cấp Ấn Độ đã được xác minh để nhập khẩu liền mạch. Giá cạnh tranh, đảm bảo chất lượng và logistics tích hợp.",
      getStarted: "Bắt đầu ngay hôm nay",
      connectWithSuppliers: "Kết nối với nhà cung cấp",
      bySubmitting: "Bằng việc gửi, bạn đồng ý với điều khoản dịch vụ và chính sách bảo mật của chúng tôi",
    },
    form: {
      fullName: "Họ và tên",
      businessEmail: "Email doanh nghiệp",
      phone: "Điện thoại (kèm mã quốc gia)",
      companyName: "Tên công ty",
      monthlyVolume: "Khối lượng mua hàng tháng",
      selectVolume: "Chọn phạm vi khối lượng",
      interestedCategories: "Danh mục quan tâm",
      under10k: "Dưới $10,000",
      range10k50k: "$10,000 - $50,000",
      range50k100k: "$50,000 - $100,000",
      range100k500k: "$100,000 - $500,000",
      over500k: "Trên $500,000",
    },
    sections: {
      topCategories: "Danh mục xuất khẩu hàng đầu sang",
      whySource: "Tại sao mua hàng từ Ấn Độ qua ProcureSaathi?",
      verifiedSuppliers: "Nhà cung cấp đã xác minh",
      verifiedSuppliersDesc: "Tất cả nhà cung cấp được xác minh với giấy phép xuất khẩu, chứng nhận và hồ sơ theo dõi phù hợp",
      integratedLogistics: "Logistics tích hợp",
      integratedLogisticsDesc: "Giải pháp vận chuyển từ đầu đến cuối từ nhà máy đến cửa nhà bạn với hỗ trợ hải quan",
      realTimeTracking: "Theo dõi thời gian thực",
      realTimeTrackingDesc: "Theo dõi lô hàng của bạn theo thời gian thực từ khi gửi đến khi giao tại cảng đích",
      readyToStart: "Sẵn sàng bắt đầu mua hàng từ Ấn Độ?",
      joinThousands: "Tham gia cùng hàng ngàn nhà nhập khẩu tiết kiệm 25-40% chi phí mua hàng",
      createFreeAccount: "Tạo tài khoản miễn phí",
      browseCategories: "Duyệt danh mục",
    },
    stats: {
      tradeVolume: "Khối lượng thương mại",
      activeSuppliers: "Nhà cung cấp hoạt động",
      avgSavings: "Tiết kiệm trung bình",
      avgDelivery: "Thời gian giao hàng TB",
      countriesServed: "Quốc gia phục vụ",
      qualityCompliance: "Tuân thủ chất lượng",
    },
    nav: {
      home: "Trang chủ",
      categories: "Danh mục",
      suppliers: "Nhà cung cấp",
      about: "Về chúng tôi",
      contact: "Liên hệ",
      login: "Đăng nhập",
      signup: "Đăng ký",
    },
    common: {
      loading: "Đang tải...",
      error: "Lỗi",
      success: "Thành công",
      submit: "Gửi",
      cancel: "Hủy",
      save: "Lưu",
      delete: "Xóa",
      edit: "Sửa",
      view: "Xem",
      search: "Tìm kiếm",
      filter: "Lọc",
      noResults: "Không tìm thấy kết quả",
    },
  },

  // Turkish
  tr: {
    hero: {
      headline: "Hindistan'dan Kaliteli Ürünler Tedarik Edin",
      description: "Sorunsuz ithalat için doğrulanmış Hint tedarikçileriyle bağlantı kurun. Rekabetçi fiyatlar, kalite güvencesi ve entegre lojistik.",
      getStarted: "Bugün Başlayın",
      connectWithSuppliers: "Tedarikçilerle Bağlantı Kurun",
      bySubmitting: "Göndererek, hizmet şartlarımızı ve gizlilik politikamızı kabul etmiş olursunuz",
    },
    form: {
      fullName: "Ad Soyad",
      businessEmail: "İş E-postası",
      phone: "Telefon (ülke kodu ile)",
      companyName: "Şirket Adı",
      monthlyVolume: "Aylık Tedarik Hacmi",
      selectVolume: "Hacim aralığı seçin",
      interestedCategories: "İlgilenilen Kategoriler",
      under10k: "$10,000'ın altında",
      range10k50k: "$10,000 - $50,000",
      range50k100k: "$50,000 - $100,000",
      range100k500k: "$100,000 - $500,000",
      over500k: "$500,000'ın üzerinde",
    },
    sections: {
      topCategories: "için En İyi İhracat Kategorileri",
      whySource: "Neden ProcureSaathi üzerinden Hindistan'dan tedarik etmelisiniz?",
      verifiedSuppliers: "Doğrulanmış Tedarikçiler",
      verifiedSuppliersDesc: "Tüm tedarikçiler uygun ihracat lisansları, sertifikalar ve geçmiş performans kayıtlarıyla doğrulanmıştır",
      integratedLogistics: "Entegre Lojistik",
      integratedLogisticsDesc: "Fabrikadan kapınıza kadar gümrük desteği ile uçtan uca nakliye çözümleri",
      realTimeTracking: "Gerçek Zamanlı Takip",
      realTimeTrackingDesc: "Gönderimlerinizi sevkiyattan varış limanına teslimata kadar gerçek zamanlı takip edin",
      readyToStart: "Hindistan'dan tedarike başlamaya hazır mısınız?",
      joinThousands: "Tedarik maliyetlerinde %25-40 tasarruf eden binlerce ithalatçıya katılın",
      createFreeAccount: "Ücretsiz Hesap Oluştur",
      browseCategories: "Kategorilere Göz At",
    },
    stats: {
      tradeVolume: "Ticaret Hacmi",
      activeSuppliers: "Aktif Tedarikçiler",
      avgSavings: "Ort. Tasarruf",
      avgDelivery: "Ort. Teslimat Süresi",
      countriesServed: "Hizmet Verilen Ülkeler",
      qualityCompliance: "Kalite Uyumu",
    },
    nav: {
      home: "Ana Sayfa",
      categories: "Kategoriler",
      suppliers: "Tedarikçiler",
      about: "Hakkımızda",
      contact: "İletişim",
      login: "Giriş",
      signup: "Kayıt Ol",
    },
    common: {
      loading: "Yükleniyor...",
      error: "Hata",
      success: "Başarılı",
      submit: "Gönder",
      cancel: "İptal",
      save: "Kaydet",
      delete: "Sil",
      edit: "Düzenle",
      view: "Görüntüle",
      search: "Ara",
      filter: "Filtrele",
      noResults: "Sonuç bulunamadı",
    },
  },

  // Swedish
  sv: {
    hero: {
      headline: "Köp kvalitetsprodukter från Indien",
      description: "Anslut till verifierade indiska leverantörer för sömlös import. Konkurrenskraftiga priser, kvalitetssäkring och integrerad logistik.",
      getStarted: "Kom igång idag",
      connectWithSuppliers: "Anslut till leverantörer",
      bySubmitting: "Genom att skicka godkänner du våra användarvillkor och integritetspolicy",
    },
    form: {
      fullName: "Fullständigt namn",
      businessEmail: "Företags-e-post",
      phone: "Telefon (med landskod)",
      companyName: "Företagsnamn",
      monthlyVolume: "Månatlig inköpsvolym",
      selectVolume: "Välj volymintervall",
      interestedCategories: "Intresserade kategorier",
      under10k: "Under 100 000 kr",
      range10k50k: "100 000 - 500 000 kr",
      range50k100k: "500 000 - 1 000 000 kr",
      range100k500k: "1 000 000 - 5 000 000 kr",
      over500k: "Över 5 000 000 kr",
    },
    sections: {
      topCategories: "Topp exportkategorier till",
      whySource: "Varför köpa från Indien via ProcureSaathi?",
      verifiedSuppliers: "Verifierade leverantörer",
      verifiedSuppliersDesc: "Alla leverantörer är verifierade med lämpliga exportlicenser, certifieringar och meritlista",
      integratedLogistics: "Integrerad logistik",
      integratedLogisticsDesc: "End-to-end fraktlösningar från fabrik till din dörr med tullstöd",
      realTimeTracking: "Realtidsspårning",
      realTimeTrackingDesc: "Spåra dina försändelser i realtid från avsändning till leverans vid destinationshamn",
      readyToStart: "Redo att börja köpa från Indien?",
      joinThousands: "Gå med tusentals importörer som sparar 25-40% på inköpskostnader",
      createFreeAccount: "Skapa gratis konto",
      browseCategories: "Bläddra kategorier",
    },
    stats: {
      tradeVolume: "Handelsvolym",
      activeSuppliers: "Aktiva leverantörer",
      avgSavings: "Genomsn. besparing",
      avgDelivery: "Genomsn. leveranstid",
      countriesServed: "Länder betjänade",
      qualityCompliance: "Kvalitetsefterlevnad",
    },
    nav: {
      home: "Hem",
      categories: "Kategorier",
      suppliers: "Leverantörer",
      about: "Om oss",
      contact: "Kontakt",
      login: "Logga in",
      signup: "Registrera",
    },
    common: {
      loading: "Laddar...",
      error: "Fel",
      success: "Framgång",
      submit: "Skicka",
      cancel: "Avbryt",
      save: "Spara",
      delete: "Radera",
      edit: "Redigera",
      view: "Visa",
      search: "Sök",
      filter: "Filtrera",
      noResults: "Inga resultat hittades",
    },
  },

  // Norwegian
  no: {
    hero: {
      headline: "Kjøp kvalitetsprodukter fra India",
      description: "Koble til verifiserte indiske leverandører for sømløs import. Konkurransedyktige priser, kvalitetssikring og integrert logistikk.",
      getStarted: "Kom i gang i dag",
      connectWithSuppliers: "Koble til leverandører",
      bySubmitting: "Ved å sende inn godtar du våre brukervilkår og personvernregler",
    },
    form: {
      fullName: "Fullt navn",
      businessEmail: "Bedrifts-e-post",
      phone: "Telefon (med landskode)",
      companyName: "Firmanavn",
      monthlyVolume: "Månedlig innkjøpsvolum",
      selectVolume: "Velg volumområde",
      interestedCategories: "Interesserte kategorier",
      under10k: "Under 100 000 kr",
      range10k50k: "100 000 - 500 000 kr",
      range50k100k: "500 000 - 1 000 000 kr",
      range100k500k: "1 000 000 - 5 000 000 kr",
      over500k: "Over 5 000 000 kr",
    },
    sections: {
      topCategories: "Topp eksportkategorier til",
      whySource: "Hvorfor kjøpe fra India via ProcureSaathi?",
      verifiedSuppliers: "Verifiserte leverandører",
      verifiedSuppliersDesc: "Alle leverandører er verifisert med passende eksportlisenser, sertifiseringer og merittliste",
      integratedLogistics: "Integrert logistikk",
      integratedLogisticsDesc: "End-to-end fraktløsninger fra fabrikk til din dør med tollstøtte",
      realTimeTracking: "Sanntidssporing",
      realTimeTrackingDesc: "Spor forsendelsene dine i sanntid fra avsendelse til levering ved destinasjonshavn",
      readyToStart: "Klar til å begynne å kjøpe fra India?",
      joinThousands: "Bli med tusenvis av importører som sparer 25-40% på innkjøpskostnader",
      createFreeAccount: "Opprett gratis konto",
      browseCategories: "Bla gjennom kategorier",
    },
    stats: {
      tradeVolume: "Handelsvolum",
      activeSuppliers: "Aktive leverandører",
      avgSavings: "Gj.sn. besparelse",
      avgDelivery: "Gj.sn. leveringstid",
      countriesServed: "Land betjent",
      qualityCompliance: "Kvalitetsoverholdelse",
    },
    nav: {
      home: "Hjem",
      categories: "Kategorier",
      suppliers: "Leverandører",
      about: "Om oss",
      contact: "Kontakt",
      login: "Logg inn",
      signup: "Registrer",
    },
    common: {
      loading: "Laster...",
      error: "Feil",
      success: "Suksess",
      submit: "Send",
      cancel: "Avbryt",
      save: "Lagre",
      delete: "Slett",
      edit: "Rediger",
      view: "Vis",
      search: "Søk",
      filter: "Filtrer",
      noResults: "Ingen resultater funnet",
    },
  },

  // Greek
  el: {
    hero: {
      headline: "Προμηθευτείτε ποιοτικά προϊόντα από την Ινδία",
      description: "Συνδεθείτε με επαληθευμένους Ινδούς προμηθευτές για απρόσκοπτη εισαγωγή. Ανταγωνιστικές τιμές, διασφάλιση ποιότητας και ολοκληρωμένη εφοδιαστική.",
      getStarted: "Ξεκινήστε σήμερα",
      connectWithSuppliers: "Σύνδεση με προμηθευτές",
      bySubmitting: "Υποβάλλοντας, συμφωνείτε με τους όρους υπηρεσίας και την πολιτική απορρήτου μας",
    },
    form: {
      fullName: "Πλήρες όνομα",
      businessEmail: "Επαγγελματικό email",
      phone: "Τηλέφωνο (με κωδικό χώρας)",
      companyName: "Επωνυμία εταιρείας",
      monthlyVolume: "Μηνιαίος όγκος προμηθειών",
      selectVolume: "Επιλέξτε εύρος όγκου",
      interestedCategories: "Κατηγορίες ενδιαφέροντος",
      under10k: "Κάτω από €10.000",
      range10k50k: "€10.000 - €50.000",
      range50k100k: "€50.000 - €100.000",
      range100k500k: "€100.000 - €500.000",
      over500k: "Πάνω από €500.000",
    },
    sections: {
      topCategories: "Κορυφαίες κατηγορίες εξαγωγών προς",
      whySource: "Γιατί να προμηθευτείτε από την Ινδία μέσω ProcureSaathi;",
      verifiedSuppliers: "Επαληθευμένοι προμηθευτές",
      verifiedSuppliersDesc: "Όλοι οι προμηθευτές έχουν επαληθευτεί με κατάλληλες άδειες εξαγωγής, πιστοποιήσεις και ιστορικό",
      integratedLogistics: "Ολοκληρωμένη εφοδιαστική",
      integratedLogisticsDesc: "Λύσεις αποστολής από άκρη σε άκρη από το εργοστάσιο μέχρι την πόρτα σας με τελωνειακή υποστήριξη",
      realTimeTracking: "Παρακολούθηση σε πραγματικό χρόνο",
      realTimeTrackingDesc: "Παρακολουθήστε τις αποστολές σας σε πραγματικό χρόνο από την αποστολή μέχρι την παράδοση στο λιμάνι προορισμού",
      readyToStart: "Έτοιμοι να ξεκινήσετε τις προμήθειες από την Ινδία;",
      joinThousands: "Εγγραφείτε με χιλιάδες εισαγωγείς που εξοικονομούν 25-40% στο κόστος προμηθειών",
      createFreeAccount: "Δημιουργία δωρεάν λογαριασμού",
      browseCategories: "Περιήγηση κατηγοριών",
    },
    stats: {
      tradeVolume: "Όγκος εμπορίου",
      activeSuppliers: "Ενεργοί προμηθευτές",
      avgSavings: "Μέση εξοικονόμηση",
      avgDelivery: "Μέσος χρόνος παράδοσης",
      countriesServed: "Χώρες που εξυπηρετούνται",
      qualityCompliance: "Συμμόρφωση ποιότητας",
    },
    nav: {
      home: "Αρχική",
      categories: "Κατηγορίες",
      suppliers: "Προμηθευτές",
      about: "Σχετικά με εμάς",
      contact: "Επικοινωνία",
      login: "Σύνδεση",
      signup: "Εγγραφή",
    },
    common: {
      loading: "Φόρτωση...",
      error: "Σφάλμα",
      success: "Επιτυχία",
      submit: "Υποβολή",
      cancel: "Ακύρωση",
      save: "Αποθήκευση",
      delete: "Διαγραφή",
      edit: "Επεξεργασία",
      view: "Προβολή",
      search: "Αναζήτηση",
      filter: "Φίλτρο",
      noResults: "Δεν βρέθηκαν αποτελέσματα",
    },
  },

  // Nepali
  ne: {
    hero: {
      headline: "भारतबाट गुणस्तरीय उत्पादनहरू प्राप्त गर्नुहोस्",
      description: "सहज आयातको लागि प्रमाणित भारतीय आपूर्तिकर्ताहरूसँग जोडिनुहोस्। प्रतिस्पर्धी मूल्य, गुणस्तर सुनिश्चितता, र एकीकृत लजिस्टिक्स।",
      getStarted: "आज नै सुरु गर्नुहोस्",
      connectWithSuppliers: "आपूर्तिकर्ताहरूसँग जोडिनुहोस्",
      bySubmitting: "पेश गरेर, तपाईं हाम्रो सेवा सर्तहरू र गोपनीयता नीतिमा सहमत हुनुहुन्छ",
    },
    form: {
      fullName: "पूरा नाम",
      businessEmail: "व्यापार इमेल",
      phone: "फोन (देश कोड सहित)",
      companyName: "कम्पनीको नाम",
      monthlyVolume: "मासिक खरीद मात्रा",
      selectVolume: "मात्रा दायरा छान्नुहोस्",
      interestedCategories: "रुचिका श्रेणीहरू",
      under10k: "रु. १० लाख भन्दा कम",
      range10k50k: "रु. १० लाख - ५० लाख",
      range50k100k: "रु. ५० लाख - १ करोड",
      range100k500k: "रु. १ करोड - ५ करोड",
      over500k: "रु. ५ करोड भन्दा बढी",
    },
    sections: {
      topCategories: "मा शीर्ष निर्यात श्रेणीहरू",
      whySource: "किन ProcureSaathi मार्फत भारतबाट खरिद गर्ने?",
      verifiedSuppliers: "प्रमाणित आपूर्तिकर्ताहरू",
      verifiedSuppliersDesc: "सबै आपूर्तिकर्ताहरू उचित निर्यात लाइसेन्स, प्रमाणपत्र र ट्र्याक रेकर्डसँग प्रमाणित छन्",
      integratedLogistics: "एकीकृत लजिस्टिक्स",
      integratedLogisticsDesc: "कारखानाबाट तपाईंको ढोकासम्म कस्टम सहयोग सहित एन्ड-टु-एन्ड शिपिङ समाधान",
      realTimeTracking: "वास्तविक-समय ट्र्याकिङ",
      realTimeTrackingDesc: "डिस्प्याचबाट गन्तव्य बन्दरगाहमा डेलिभरी सम्म वास्तविक-समयमा तपाईंको शिपमेन्ट ट्र्याक गर्नुहोस्",
      readyToStart: "भारतबाट खरिद सुरु गर्न तयार हुनुहुन्छ?",
      joinThousands: "हजारौं आयातकर्ताहरूसँग जोडिनुहोस् जसले खरिद लागतमा २५-४०% बचत गर्छन्",
      createFreeAccount: "नि:शुल्क खाता बनाउनुहोस्",
      browseCategories: "श्रेणीहरू हेर्नुहोस्",
    },
    stats: {
      tradeVolume: "व्यापार मात्रा",
      activeSuppliers: "सक्रिय आपूर्तिकर्ताहरू",
      avgSavings: "औसत बचत",
      avgDelivery: "औसत डेलिभरी समय",
      countriesServed: "सेवा गरिएका देशहरू",
      qualityCompliance: "गुणस्तर अनुपालन",
    },
    nav: {
      home: "गृहपृष्ठ",
      categories: "श्रेणीहरू",
      suppliers: "आपूर्तिकर्ताहरू",
      about: "हाम्रो बारेमा",
      contact: "सम्पर्क",
      login: "लगइन",
      signup: "साइन अप",
    },
    common: {
      loading: "लोड हुँदैछ...",
      error: "त्रुटि",
      success: "सफलता",
      submit: "पेश गर्नुहोस्",
      cancel: "रद्द गर्नुहोस्",
      save: "बचत गर्नुहोस्",
      delete: "मेटाउनुहोस्",
      edit: "सम्पादन गर्नुहोस्",
      view: "हेर्नुहोस्",
      search: "खोज्नुहोस्",
      filter: "फिल्टर",
      noResults: "कुनै नतिजा फेला परेन",
    },
  },

  // Malay
  ms: {
    hero: {
      headline: "Dapatkan Produk Berkualiti dari India",
      description: "Berhubung dengan pembekal India yang disahkan untuk import yang lancar. Harga kompetitif, jaminan kualiti dan logistik bersepadu.",
      getStarted: "Mulakan Hari Ini",
      connectWithSuppliers: "Hubungi Pembekal",
      bySubmitting: "Dengan menghantar, anda bersetuju dengan terma perkhidmatan dan dasar privasi kami",
    },
    form: {
      fullName: "Nama Penuh",
      businessEmail: "E-mel Perniagaan",
      phone: "Telefon (dengan kod negara)",
      companyName: "Nama Syarikat",
      monthlyVolume: "Jumlah Perolehan Bulanan",
      selectVolume: "Pilih julat jumlah",
      interestedCategories: "Kategori Yang Diminati",
      under10k: "Di bawah RM40,000",
      range10k50k: "RM40,000 - RM200,000",
      range50k100k: "RM200,000 - RM400,000",
      range100k500k: "RM400,000 - RM2,000,000",
      over500k: "Lebih RM2,000,000",
    },
    sections: {
      topCategories: "Kategori Eksport Utama ke",
      whySource: "Mengapa Mendapatkan dari India melalui ProcureSaathi?",
      verifiedSuppliers: "Pembekal Yang Disahkan",
      verifiedSuppliersDesc: "Semua pembekal disahkan dengan lesen eksport, pensijilan dan rekod prestasi yang sesuai",
      integratedLogistics: "Logistik Bersepadu",
      integratedLogisticsDesc: "Penyelesaian penghantaran hujung ke hujung dari kilang ke pintu anda dengan sokongan kastam",
      realTimeTracking: "Penjejakan Masa Nyata",
      realTimeTrackingDesc: "Jejaki penghantaran anda dalam masa nyata dari penghantaran hingga penghantaran di pelabuhan destinasi",
      readyToStart: "Bersedia Untuk Mula Mendapatkan dari India?",
      joinThousands: "Sertai beribu-ribu pengimport yang menjimatkan 25-40% kos perolehan",
      createFreeAccount: "Cipta Akaun Percuma",
      browseCategories: "Layari Kategori",
    },
    stats: {
      tradeVolume: "Jumlah Dagangan",
      activeSuppliers: "Pembekal Aktif",
      avgSavings: "Penjimatan Purata",
      avgDelivery: "Masa Penghantaran Purata",
      countriesServed: "Negara Dilayani",
      qualityCompliance: "Pematuhan Kualiti",
    },
    nav: {
      home: "Laman Utama",
      categories: "Kategori",
      suppliers: "Pembekal",
      about: "Tentang Kami",
      contact: "Hubungi",
      login: "Log Masuk",
      signup: "Daftar",
    },
    common: {
      loading: "Memuatkan...",
      error: "Ralat",
      success: "Berjaya",
      submit: "Hantar",
      cancel: "Batal",
      save: "Simpan",
      delete: "Padam",
      edit: "Sunting",
      view: "Lihat",
      search: "Cari",
      filter: "Tapis",
      noResults: "Tiada hasil ditemui",
    },
  },

  // Filipino/Tagalog
  tl: {
    hero: {
      headline: "Kumuha ng Mga Kalidad na Produkto mula sa India",
      description: "Kumonekta sa mga verified na Indian supplier para sa seamless na pag-import. Competitive na presyo, quality assurance, at integrated logistics.",
      getStarted: "Magsimula Ngayon",
      connectWithSuppliers: "Kumonekta sa mga Supplier",
      bySubmitting: "Sa pagsusumite, sumasang-ayon ka sa aming mga terms of service at privacy policy",
    },
    form: {
      fullName: "Buong Pangalan",
      businessEmail: "Business Email",
      phone: "Telepono (may country code)",
      companyName: "Pangalan ng Kompanya",
      monthlyVolume: "Buwanang Volume ng Sourcing",
      selectVolume: "Pumili ng volume range",
      interestedCategories: "Mga Kategoryang Interesado",
      under10k: "Mas mababa sa ₱500,000",
      range10k50k: "₱500,000 - ₱2,500,000",
      range50k100k: "₱2,500,000 - ₱5,000,000",
      range100k500k: "₱5,000,000 - ₱25,000,000",
      over500k: "Higit sa ₱25,000,000",
    },
    sections: {
      topCategories: "Mga Nangungunang Export Categories para sa",
      whySource: "Bakit Kumuha mula sa India sa pamamagitan ng ProcureSaathi?",
      verifiedSuppliers: "Mga Verified na Supplier",
      verifiedSuppliersDesc: "Lahat ng supplier ay verified na may tamang export license, certification, at track record",
      integratedLogistics: "Integrated Logistics",
      integratedLogisticsDesc: "End-to-end shipping solutions mula factory hanggang sa iyong pintuan na may customs support",
      realTimeTracking: "Real-Time Tracking",
      realTimeTrackingDesc: "I-track ang iyong mga shipment sa real-time mula dispatch hanggang delivery sa destination port",
      readyToStart: "Handa Ka Na Bang Magsimulang Kumuha mula sa India?",
      joinThousands: "Sumali sa libu-libong importer na nakakatipid ng 25-40% sa procurement costs",
      createFreeAccount: "Gumawa ng Libreng Account",
      browseCategories: "Tingnan ang mga Kategorya",
    },
    stats: {
      tradeVolume: "Trade Volume",
      activeSuppliers: "Mga Active na Supplier",
      avgSavings: "Ave. Savings",
      avgDelivery: "Ave. Delivery Time",
      countriesServed: "Mga Bansang Serbisyuhan",
      qualityCompliance: "Quality Compliance",
    },
    nav: {
      home: "Home",
      categories: "Mga Kategorya",
      suppliers: "Mga Supplier",
      about: "Tungkol sa Amin",
      contact: "Makipag-ugnayan",
      login: "Mag-login",
      signup: "Mag-signup",
    },
    common: {
      loading: "Naglo-load...",
      error: "Error",
      success: "Tagumpay",
      submit: "Isumite",
      cancel: "Kanselahin",
      save: "I-save",
      delete: "I-delete",
      edit: "I-edit",
      view: "Tingnan",
      search: "Maghanap",
      filter: "I-filter",
      noResults: "Walang nakitang resulta",
    },
  },
};

// Get default language for a country based on traffic data
export const getDefaultLanguage = (country: string): Language => {
  const countryLanguageMap: Record<string, Language> = {
    india: 'en',
    usa: 'en',
    uk: 'en',
    france: 'fr',
    netherlands: 'nl',
    romania: 'ro',
    spain: 'es',
    canada: 'en',
    germany: 'de',
    uae: 'ar',
    japan: 'ja',
    china: 'zh',
    portugal: 'pt',
    italy: 'it',
    russia: 'ru',
    thailand: 'th',
    vietnam: 'vi',
    turkey: 'tr',
    sweden: 'sv',
    norway: 'no',
    greece: 'el',
    nepal: 'ne',
    malaysia: 'ms',
    philippines: 'tl',
    "saudi-arabia": 'ar',
    qatar: 'ar',
    oman: 'ar',
    kuwait: 'ar',
    australia: 'en',
    "new-zealand": 'en',
    "south-africa": 'en',
    argentina: 'es',
    mexico: 'es',
    "sri-lanka": 'en',
    taiwan: 'zh',
    austria: 'de',
    switzerland: 'de',
  };
  return countryLanguageMap[country.toLowerCase()] || 'en';
};

// RTL languages
export const isRTL = (lang: Language): boolean => {
  return lang === 'ar';
};

// Get language name in its native form
export const getLanguageName = (lang: Language): string => {
  const names: Record<Language, string> = {
    en: 'English',
    hi: 'हिन्दी',
    fr: 'Français',
    nl: 'Nederlands',
    ro: 'Română',
    es: 'Español',
    de: 'Deutsch',
    ar: 'العربية',
    ja: '日本語',
    zh: '中文',
    pt: 'Português',
    it: 'Italiano',
    ru: 'Русский',
    th: 'ไทย',
    vi: 'Tiếng Việt',
    tr: 'Türkçe',
    sv: 'Svenska',
    no: 'Norsk',
    el: 'Ελληνικά',
    ne: 'नेपाली',
    ms: 'Bahasa Melayu',
    tl: 'Filipino',
  };
  return names[lang];
};

// Get all supported languages
export const getSupportedLanguages = (): Language[] => {
  return ['en', 'hi', 'fr', 'nl', 'ro', 'es', 'de', 'ar', 'ja', 'zh', 'pt', 'it', 'ru', 'th', 'vi', 'tr', 'sv', 'no', 'el', 'ne', 'ms', 'tl'];
};

// Get available languages for a country
export const getCountryLanguages = (country: string): Language[] => {
  const countryLanguagesMap: Record<string, Language[]> = {
    india: ['en', 'hi'],
    usa: ['en', 'es'],
    uk: ['en'],
    france: ['fr', 'en'],
    netherlands: ['nl', 'en'],
    romania: ['ro', 'en'],
    spain: ['es', 'en'],
    canada: ['en', 'fr'],
    germany: ['de', 'en'],
    uae: ['ar', 'en'],
    japan: ['ja', 'en'],
    china: ['zh', 'en'],
    portugal: ['pt', 'en'],
    italy: ['it', 'en'],
    russia: ['ru', 'en'],
    thailand: ['th', 'en'],
    vietnam: ['vi', 'en'],
    turkey: ['tr', 'en'],
    sweden: ['sv', 'en'],
    norway: ['no', 'en'],
    greece: ['el', 'en'],
    nepal: ['ne', 'en', 'hi'],
    malaysia: ['ms', 'en'],
    philippines: ['tl', 'en'],
    "saudi-arabia": ['ar', 'en'],
    qatar: ['ar', 'en'],
    oman: ['ar', 'en'],
    kuwait: ['ar', 'en'],
    australia: ['en'],
    "new-zealand": ['en'],
    "south-africa": ['en'],
    argentina: ['es', 'en'],
    mexico: ['es', 'en'],
    "sri-lanka": ['en'],
    taiwan: ['zh', 'en'],
    austria: ['de', 'en'],
    switzerland: ['de', 'fr', 'it', 'en'],
    bhutan: ['en'],
    maldives: ['en'],
    togo: ['fr', 'en'],
    malawi: ['en'],
    uganda: ['en'],
    zimbabwe: ['en'],
  };
  return countryLanguagesMap[country.toLowerCase()] || ['en'];
};
